// =============================================================================
// TESTIMONIAL SECTION COMPONENT
// =============================================================================

import React from 'react';
import { Star, Quote } from 'lucide-react';
import type { ContentComponent } from '@/types';

interface TestimonialSectionProps {
  component: ContentComponent;
}

/**
 * Testimonial Section Component
 */
export const TestimonialSection: React.FC<TestimonialSectionProps> = ({ component }) => {
  return <TestimonialSectionImpl component={component} />;
};

/**
 * Render stars for rating
 */
const renderStars = (rating: number) => {
  return Array.from({ length: 5 }, (_, index) => (
    <Star
      key={index}
      size={16}
      className={`star ${index < rating ? 'filled' : 'empty'}`}
    />
  ));
};

/**
 * Testimonial Section Implementation
 */
const TestimonialSectionImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const testimonials = metadata?.testimonials || [];
  const columns = metadata?.columns || 3;
  
  return (
    <section id={id} className="content-section testimonial-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`testimonial-grid grid-cols-${columns}`}>
          {testimonials.map((testimonial: any, index: number) => (
            <div key={index} className="testimonial-card">
              <div className="testimonial-quote">
                <Quote size={24} className="quote-icon" />
              </div>
              
              <div className="testimonial-content">
                <p>"{testimonial.body}"</p>
              </div>
              
              {testimonial.rating && (
                <div className="testimonial-rating">
                  {renderStars(testimonial.rating)}
                </div>
              )}
              
              <div className="testimonial-author">
                <div className="author-info">
                  {testimonial.avatar && (
                    <div className="author-avatar">
                      <img src={testimonial.avatar} alt={testimonial.name} loading="lazy" />
                    </div>
                  )}
                  
                  <div className="author-details">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-title">
                      {testimonial.role}
                      {testimonial.company && (
                        <>
                          {' at '}
                          <span className="author-company">{testimonial.company}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { TestimonialSectionImpl };

